<template>
  <div class="home-container">
    <header class="header">
      <div class="logo">
        <img src="@/assets/logo.png" alt="WMS系统logo" class="logo-img" />
        <span class="app-name">安徽和丰WMS系统</span>
      </div>
      <div class="user-info">
        <el-tag effect="dark" round class="username-tag">
          {{ username || "未知用户" }}
        </el-tag>
      </div>
    </header>

    <main class="content">
      <h1>欢迎使用安徽和丰WMS系统</h1>

      <el-card class="common-functions" shadow="never">
        <div class="common-header">
          <h2>常用功能</h2>
          <el-button
            :type="isEditMode ? 'success' : 'primary'"
            size="small"
            round
            class="edit-btn"
            @click="toggleEditMode"
          >
            {{ isEditMode ? "完成" : "编辑" }}
          </el-button>
        </div>

        <div v-if="commonFunctions.length > 0" class="common-list">
          <div
            v-for="(item, index) in commonFunctions"
            :key="item.path + '-' + index"
            class="common-item"
            :class="{ dragging: draggedItem === index }"
            draggable="true"
            @click="!isEditMode && navigateTo(item.path)"
            @dragstart="dragStart(index)"
            @dragenter="dragEnter(index)"
            @dragover.prevent
            @dragleave="dragLeave"
            @drop="dragDrop(index)"
          >
            <div class="common-icon">
              <img
                :src="getCommonIconPath(item.icon, item.name)"
                :alt="item.name"
                class="common-icon-img"
                @error="handleImgError"
              />
            </div>
            <div class="common-name">{{ item.name }}</div>
            <el-button
              v-if="isEditMode"
              class="delete-btn"
              type="danger"
              :icon="Close"
              circle
              size="small"
              @click.stop="removeCommonFunction(index)"
            />
          </div>
        </div>

        <el-empty
          v-else
          :description="
            isEditMode
              ? '暂无常用功能，点击下方功能添加'
              : '点击编辑添加常用功能'
          "
          :image-size="40"
        />

        <el-alert
          v-if="isEditMode"
          title="点击下方功能卡片添加到常用"
          type="info"
          show-icon
          :closable="false"
          class="add-tip"
        />
      </el-card>

      <div class="category-tabs">
        <div
          v-for="(category, index) in categories"
          :key="category.name"
          :class="['tab-item', { active: activeCategory === index }]"
          @click="switchCategory(index)"
        >
          {{ category.name }}
        </div>
      </div>

      <div
        ref="scrollContainer"
        class="card-scroll-container"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        @scroll="handleScroll"
      >
        <div class="card-wrapper">
          <div
            v-for="category in categories"
            :key="category.name"
            class="card-panel"
          >
            <div class="card-container">
              <div
                v-for="card in visibleCards(category)"
                :key="card.path"
                class="card"
                :class="{ added: isInCommon(card.commonPath || card.path) }"
                @click="
                  handleCardClick(card.path, card.commonName, card.iconKey)
                "
              >
                <div class="card-icon">
                  <img
                    :src="card.iconSrc"
                    :alt="card.title"
                    class="card-icon-img"
                  />
                </div>
                <div class="card-title">{{ card.title }}</div>
                <div class="card-desc">{{ card.desc }}</div>
                <el-tag
                  v-if="
                    isEditMode && !isInCommon(card.commonPath || card.path)
                  "
                  class="add-tag"
                  type="primary"
                  size="small"
                  effect="dark"
                  round
                >
                  + 添加
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="indicator-dots">
        <div
          v-for="(category, index) in categories"
          :key="'dot-' + category.name"
          :class="['dot', { active: activeCategory === index }]"
          @click="switchCategory(index)"
        />
      </div>
    </main>

    <nav class="tab-bar" aria-label="底部导航">
      <div
        v-for="tab in bottomTabs"
        :key="tab.key"
        class="tab-item"
        :class="{ active: activeTab === tab.key }"
        @click="navigateTo(tab.key)"
      >
        <img :src="tab.icon" :alt="tab.label" class="tab-icon" />
        <span class="tab-text">{{ tab.label }}</span>
      </div>
    </nav>

    <footer class="footer">
      <p>2026 安徽和丰信息化系统 版权所有 6.6.01</p>
    </footer>
  </div>
</template>

<script>
import { computed, ref, onMounted, nextTick, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { Close } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { getUicAuthUserInfo } from "@/utils/auth";
import { getMobileMenuList } from "@/utils/dynamicRoutes";

const DEFAULT_ICON = "首页";

const ICON_MAP = {
  扫码入库: require("@/assets/收货.svg"),
  入库订单: require("@/assets/收货.svg"),
  新增收货单据: require("@/assets/收货.svg"),
  创单及收货: require("@/assets/收货.svg"),
  PLS扫码收货: require("@/assets/扫码.svg"),
  PLS扫码入库: require("@/assets/扫码.svg"),
  TCL扫码收货: require("@/assets/扫码.svg"),
  TCL扫码出库: require("@/assets/扫码.svg"),
  扫码出库: require("@/assets/出库列表.svg"),
  出库列表: require("@/assets/出库列表.svg"),
  出库单: require("@/assets/出库单.svg"),
  出库管理: require("@/assets/出库单.svg"),
  退货出库: require("@/assets/退货出库.svg"),
  装车单: require("@/assets/装车单.svg"),
  扫码盘点: require("@/assets/扫码盘点.svg"),
  项目组盘点: require("@/assets/扫码盘点.svg"),
  托盘盘点: require("@/assets/扫码盘点.svg"),
  库存管理: require("@/assets/库存管理.svg"),
  库存查询: require("@/assets/库存管理.svg"),
  基础数据: require("@/assets/物料信息.svg"),
  库位库存查询: require("@/assets/库位库存.svg"),
  移位: require("@/assets/移位.svg"),
  调拨: require("@/assets/调拨.svg"),
  良不良移位: require("@/assets/zhuanhuan.svg"),
  备料: require("@/assets/备料1.svg"),
  备料列表: require("@/assets/备料1.svg"),
  PLS备料: require("@/assets/pls备料.svg"),
  首页: require("@/assets/首页.svg"),
  出库单打印: require("@/assets/备料打印.svg"),
  备料打印: require("@/assets/备料打印.svg"),
  上架: require("@/assets/上架.svg"),
  备料库位校验: require("@/assets/库位校验.svg"),
  备料箱码校验: require("@/assets/库位校验.svg"),
  物料信息: require("@/assets/物料信息.svg"),
};

const FEATURE_CATEGORIES = [
  {
    name: "入库管理",
    cards: [
      {
        path: "/mobile/warein-receive",
        title: "创单及收货",
        desc: "创建单据/收货入库",
        commonName: "创单及收货",
        iconKey: "入库订单",
        iconSrc: require("@/assets/收货.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/pls-receive",
        title: "PLS扫码入库",
        desc: "扫描送货单号收货",
        commonName: "PLS扫码收货",
        iconKey: "PLS扫码收货",
        iconSrc: require("@/assets/扫码.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/tcl-scan-receive",
        title: "TCL扫码收货",
        desc: "扫送货单/物料码收货",
        commonName: "TCL扫码收货",
        iconKey: "TCL扫码收货",
        iconSrc: require("@/assets/扫码.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/warein-putaway",
        title: "上架",
        desc: "上架任务列表",
        commonName: "上架",
        iconKey: "入库订单",
        iconSrc: require("@/assets/上架.svg"),
        needAuth: true,
      },
    ],
  },
  {
    name: "出库管理",
    cards: [
      {
        path: "/mobile/scan-wareout",
        title: "扫码出库",
        desc: "扫描送货单号出库",
        commonName: "扫码出库",
        iconKey: "扫码出库",
        iconSrc: require("@/assets/出库列表.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/tcl-scan-out",
        title: "TCL扫码出库",
        desc: "扫出库单/物料码出库",
        commonName: "TCL扫码出库",
        iconKey: "TCL扫码出库",
        iconSrc: require("@/assets/扫码.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/wareout-list",
        title: "出库列表",
        desc: "出库任务列表",
        commonName: "出库列表",
        iconKey: "出库列表",
        iconSrc: require("@/assets/出库列表.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/wareout-order?from=home",
        commonPath: "/mobile/wareout-order",
        title: "出库单",
        desc: "出库单查询",
        commonName: "出库单",
        iconKey: "出库管理",
        iconSrc: require("@/assets/出库单.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/wareout-list?return=1",
        title: "退货出库",
        desc: "退货出库任务",
        commonName: "退货出库",
        iconKey: "出库列表",
        iconSrc: require("@/assets/退货出库.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/loading-order",
        title: "装车单管理",
        desc: "管理和处理装车单",
        commonName: "装车单管理",
        iconKey: "装车单",
        iconSrc: require("@/assets/装车单.svg"),
        needAuth: true,
      },
    ],
  },
  {
    name: "库存管理",
    cards: [
      {
        path: "/mobile/stock-transfer",
        title: "移位",
        desc: "库存位置转移",
        commonName: "移位",
        iconKey: "移位",
        iconSrc: require("@/assets/移位.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/quality-transfer",
        title: "良不良移位",
        desc: "良/不良库存转换",
        commonName: "良不良移位",
        iconKey: "良不良移位",
        iconSrc: require("@/assets/zhuanhuan.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/material-secondment",
        title: "调拨",
        desc: "供应商间库存调拨",
        commonName: "调拨",
        iconKey: "调拨",
        iconSrc: require("@/assets/调拨.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/material-stock-query",
        title: "库存查询",
        desc: "查询物料库存",
        commonName: "库存查询",
        iconKey: "库存管理",
        iconSrc: require("@/assets/库存管理.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/project-team-stock-check",
        title: "项目组盘点",
        desc: "项目组库存盘点",
        commonName: "项目组盘点",
        iconKey: "扫码盘点",
        iconSrc: require("@/assets/扫码盘点.svg"),
        needAuth: true,
      },
      // 库位盘点暂不使用，先注释入口
      // {
      //   path: "/mobile/location-stock-check",
      //   title: "库位盘点",
      //   desc: "按库位进行库存盘点",
      //   commonName: "库位盘点",
      //   iconKey: "扫码盘点",
      //   iconSrc: require("@/assets/扫码盘点.svg"),
      //   needAuth: true,
      // },
      {
        path: "/mobile/warehouse-stock-check",
        title: "托盘盘点",
        desc: "按托盘进行库存盘点",
        commonName: "托盘盘点",
        iconKey: "扫码盘点",
        iconSrc: require("@/assets/扫码盘点.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/stock-ware-query",
        title: "库位查询",
        desc: "查询物料库位位置",
        commonName: "库位查询",
        iconKey: "库存管理",
        iconSrc: require("@/assets/库位库存.svg"),
        needAuth: false,
      },
    ],
  },
  {
    name: "备料管理",
    cards: [
      {
        path: "/mobile/prepare-list",
        title: "备料列表",
        desc: "备料任务列表",
        commonName: "备料列表",
        iconKey: "备料列表",
        iconSrc: require("@/assets/备料1.svg"),
        needAuth: true,
      },
      {
        path: "/mobile/pls-material-preparation-mobile",
        title: "PLS备料",
        desc: "PLS备料日志",
        commonName: "PLS备料",
        iconKey: "备料",
        iconSrc: require("@/assets/pls备料.svg"),
        needAuth: true,
      },
      {
        path: "/wareout-print-list",
        title: "出库单打印",
        desc: "查询和打印出库单",
        commonName: "备料打印",
        iconKey: "入库订单",
        iconSrc: require("@/assets/备料打印.svg"),
        needAuth: false,
      },
      {
        path: "/mobile/ware-location-check",
        title: "备料箱码校验",
        desc: "扫码校验箱码",
        commonName: "备料箱码校验",
        iconKey: "备料",
        iconSrc: require("@/assets/库位校验.svg"),
        needAuth: false,
      },
    ],
  },
  {
    name: "基础数据",
    cards: [
      {
        path: "/basic-material-search",
        title: "物料信息",
        desc: "物料信息查询",
        commonName: "基础数据",
        iconKey: "基础数据",
        iconSrc: require("@/assets/物料信息.svg"),
        needAuth: false,
      },
    ],
  },
];

const BOTTOM_TABS = [
  { key: "home", label: "首页", icon: require("@/assets/首页.svg") },
  { key: "dashboard", label: "看板", icon: require("@/assets/看板.svg") },
  { key: "profile", label: "我的", icon: require("@/assets/我的.svg") },
];

function readSavedCategory() {
  try {
    const raw = sessionStorage.getItem("activeWmsCategory");
    const value = raw == null ? 0 : JSON.parse(raw);
    const index = Number(value);
    if (
      Number.isInteger(index) &&
      index >= 0 &&
      index < FEATURE_CATEGORIES.length
    ) {
      return index;
    }
  } catch (e) {
    console.warn("读取分类缓存失败", e);
  }
  return 0;
}

export default {
  name: "HomePage",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const username = ref("");
    const activeTab = ref("home");
    const categories = FEATURE_CATEGORIES;
    const bottomTabs = BOTTOM_TABS;
    const activeCategory = ref(readSavedCategory());
    const scrollContainer = ref(null);
    const isEditMode = ref(false);
    const userId = ref("");
    const commonFunctions = ref([]);
    const draggedItem = ref(null);
    const dragOverItem = ref(null);
    const startX = ref(0);
    const startTime = ref(0);
    const isScrolling = ref(false);
    const scrollWidth = ref(0);
    const dynamicRoutes = ref([]);

    const getCommonIconPath = computed(() => {
      return (iconName, itemName) => {
        if (itemName === "PLS备料") return ICON_MAP["PLS备料"];
        return ICON_MAP[iconName] || ICON_MAP[DEFAULT_ICON];
      };
    });

    const hasRoute = (path) => {
      const pathWithoutParams = path.split("?")[0];
      return dynamicRoutes.value.some((r) => {
        const routePath = r.path.split("?")[0];
        return (
          routePath === pathWithoutParams ||
          routePath === pathWithoutParams.replace("/mobile", "") ||
          routePath === "/mobile" + pathWithoutParams
        );
      });
    };

    const visibleCards = (category) =>
      (category.cards || []).filter(
        (card) => !card.needAuth || hasRoute(card.path),
      );

    const loadDynamicMenus = async () => {
      try {
        const mobileMenus = await getMobileMenuList();
        dynamicRoutes.value = mobileMenus.filter(
          (menu) => menu.path && menu.path.startsWith("/mobile"),
        );
      } catch (error) {
        console.error("加载动态菜单失败:", error);
      }
    };

    const saveCommonFunctions = () => {
      localStorage.setItem(
        `wms_common_${userId.value}`,
        JSON.stringify(commonFunctions.value),
      );
    };

    const loadCommonFunctions = () => {
      const savedCommon = localStorage.getItem(`wms_common_${userId.value}`);
      if (!savedCommon) {
        commonFunctions.value = [];
        return;
      }
      try {
        const parsedCommon = JSON.parse(savedCommon);
        commonFunctions.value = parsedCommon.map((item) => {
          if (item.name === "PLS备料") {
            return { ...item, icon: "PLS备料" };
          }
          return { ...item, icon: item.icon || DEFAULT_ICON };
        });
        saveCommonFunctions();
      } catch (e) {
        console.warn("读取常用功能失败", e);
        commonFunctions.value = [];
      }
    };

    const toggleEditMode = () => {
      isEditMode.value = !isEditMode.value;
      if (!isEditMode.value) saveCommonFunctions();
    };

    const isInCommon = (path) => {
      // 出库单导航带 ?from=home，常用判断时忽略该参数
      const normalize = (p) =>
        String(p || "").replace("?from=home", "").replace("&from=home", "");
      const target = normalize(path);
      return commonFunctions.value.some(
        (item) => item.path === path || normalize(item.path) === target,
      );
    };

    const addCommonFunction = (path, name, icon) => {
      if (isInCommon(path)) return;
      if (commonFunctions.value.length >= 8) {
        ElMessage.warning("最多只能添加8个常用功能");
        return;
      }
      commonFunctions.value.push({
        path,
        name,
        icon: icon || DEFAULT_ICON,
      });
    };

    const removeCommonFunction = (index) => {
      commonFunctions.value.splice(index, 1);
    };

    const handleCardClick = (path, name, icon) => {
      if (isEditMode.value) {
        addCommonFunction(path, name, icon);
      } else {
        navigateTo(path);
      }
    };

    const handleImgError = (e) => {
      e.target.src = require("@/assets/首页.svg");
    };

    const handleTouchStart = (e) => {
      isScrolling.value = true;
      startX.value = e.touches[0].clientX;
      startTime.value = Date.now();
    };

    const handleTouchMove = (e) => {
      if (!isScrolling.value || !scrollContainer.value) return;
      const diffX = startX.value - e.touches[0].clientX;
      if (Math.abs(diffX) > 10) e.preventDefault();
    };

    const handleTouchEnd = (e) => {
      if (!isScrolling.value || !scrollContainer.value) return;
      const diffX = startX.value - e.changedTouches[0].clientX;
      const duration = Date.now() - startTime.value;

      if (
        diffX > 30 &&
        duration > 100 &&
        activeCategory.value < categories.length - 1
      ) {
        switchCategory(activeCategory.value + 1);
      } else if (diffX < -30 && duration > 100 && activeCategory.value > 0) {
        switchCategory(activeCategory.value - 1);
      } else {
        scrollToCurrentCategory();
      }
      isScrolling.value = false;
    };

    const handleScroll = () => {
      if (!scrollContainer.value || !scrollWidth.value) return;
      const currentIndex = Math.round(
        scrollContainer.value.scrollLeft / scrollWidth.value,
      );
      if (
        currentIndex !== activeCategory.value &&
        currentIndex >= 0 &&
        currentIndex < categories.length
      ) {
        activeCategory.value = currentIndex;
        sessionStorage.setItem(
          "activeWmsCategory",
          JSON.stringify(currentIndex),
        );
      }
    };

    const switchCategory = (index) => {
      activeCategory.value = index;
      sessionStorage.setItem("activeWmsCategory", JSON.stringify(index));
      scrollToCurrentCategory();
    };

    const scrollToCurrentCategory = () => {
      nextTick(() => {
        if (!scrollContainer.value) return;
        scrollWidth.value = scrollContainer.value.offsetWidth;
        scrollContainer.value.scrollTo({
          left: activeCategory.value * scrollWidth.value,
          behavior: "smooth",
        });
      });
    };

    const resolveNavigablePath = (path) => {
      let targetPath = path;
      if (!path.startsWith("/")) targetPath = `/${path}`;

      const [pathname, search = ""] = targetPath.split("?");
      const query = search ? `?${search}` : "";
      const exists = (p) => router.getRoutes().some((r) => r.path === p);

      if (exists(pathname)) return `${pathname}${query}`;

      if (pathname.startsWith("/mobile/")) {
        const fallback = pathname.replace(/^\/mobile/, "");
        if (exists(fallback)) return `${fallback}${query}`;
      } else {
        const mobilePath = `/mobile${pathname}`;
        if (exists(mobilePath)) return `${mobilePath}${query}`;
      }

      return `${pathname}${query}`;
    };

    const navigateTo = (path) => {
      if (["home", "dashboard", "profile"].includes(path)) {
        activeTab.value = path;
      }

      sessionStorage.setItem(
        "activeWmsCategory",
        JSON.stringify(activeCategory.value),
      );

      const targetPath = resolveNavigablePath(path);
      router.push(targetPath).catch((err) => {
        console.warn("路由跳转失败:", targetPath, err);
      });
    };

    const dragStart = (index) => {
      draggedItem.value = index;
    };

    const dragEnter = (index) => {
      dragOverItem.value = index;
    };

    const dragLeave = () => {
      dragOverItem.value = null;
    };

    const dragDrop = (index) => {
      if (draggedItem.value == null || draggedItem.value === index) {
        draggedItem.value = null;
        dragOverItem.value = null;
        return;
      }
      const items = [...commonFunctions.value];
      const [dragged] = items.splice(draggedItem.value, 1);
      items.splice(index, 0, dragged);
      commonFunctions.value = items;
      saveCommonFunctions();
      draggedItem.value = null;
      dragOverItem.value = null;
    };

    watch(
      () => route.path,
      (newPath) => {
        if (newPath === "/home" || newPath === "/") {
          activeTab.value = "home";
          activeCategory.value = readSavedCategory();
          nextTick(() => scrollToCurrentCategory());
        }
      },
      { immediate: true },
    );

    onMounted(async () => {
      const userInfo = getUicAuthUserInfo();
      if (userInfo) {
        username.value = userInfo.nickName || userInfo.username || "未知用户";
        userId.value = userInfo.id || userInfo.username || "default_user";
        loadCommonFunctions();
        await loadDynamicMenus();
      } else {
        router.push("/login");
        return;
      }

      nextTick(() => {
        if (scrollContainer.value) {
          scrollWidth.value = scrollContainer.value.offsetWidth;
          scrollToCurrentCategory();
        }
      });
    });

    return {
      Close,
      username,
      activeTab,
      categories,
      bottomTabs,
      activeCategory,
      scrollContainer,
      isEditMode,
      commonFunctions,
      draggedItem,
      dragOverItem,
      toggleEditMode,
      isInCommon,
      removeCommonFunction,
      handleCardClick,
      getCommonIconPath,
      handleImgError,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleScroll,
      switchCategory,
      visibleCards,
      navigateTo,
      dragStart,
      dragEnter,
      dragLeave,
      dragDrop,
    };
  },
};
</script>

<style scoped>
.home-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
  padding-bottom: calc(56px + env(safe-area-inset-bottom));
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background, #23b1c0);
  color: var(--header-text, #fff);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: calc(56px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.logo {
  display: flex;
  align-items: center;
  min-width: 0;
}

.logo-img {
  width: 36px;
  height: 36px;
  margin-right: 10px;
  border-radius: 8px;
  object-fit: contain;
  background-color: #fff;
  flex-shrink: 0;
}

.app-name {
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-info {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
}

.username-tag {
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  border: none;
  background: rgba(255, 255, 255, 0.18) !important;
  color: #fff !important;
}

.content {
  flex: 1;
  padding: 10px 12px;
  margin-top: calc(56px + env(safe-area-inset-top));
  padding-bottom: 24px;
  box-sizing: border-box;
}

.content h1 {
  margin: 0 0 12px;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  text-align: center;
}

.common-functions {
  width: 100%;
  margin-bottom: 12px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--card-background);
}

.common-functions :deep(.el-card__body) {
  padding: 12px;
}

.common-header {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-bottom: 8px;
}

.common-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.edit-btn {
  position: absolute;
  right: 0;
}

.common-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.common-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background-color: var(--background-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  border: 1px solid var(--border-color);
  box-sizing: border-box;
}

.common-item.dragging {
  opacity: 0.5;
  transform: scale(1.05);
}

.common-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(35, 177, 192, 0.1);
  border-radius: 50%;
  flex-shrink: 0;
}

.common-icon-img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.common-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
}

.add-tip {
  margin-top: 8px;
}

.category-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  width: 100%;
}

.category-tabs .tab-item {
  flex: 1;
  padding: 8px 0;
  background-color: var(--card-background);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
  border: 1px solid var(--border-color);
}

.category-tabs .tab-item.active {
  background-color: var(--primary-color, #23b1c0);
  border-color: var(--primary-color, #23b1c0);
  color: #fff;
}

.card-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  margin-bottom: 16px;
}

.card-scroll-container::-webkit-scrollbar {
  display: none;
}

.card-wrapper {
  display: flex;
}

.card-panel {
  flex: 0 0 auto;
  width: 100%;
  scroll-snap-align: start;
}

.card-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  width: 100%;
}

.card {
  background-color: var(--card-background);
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;
  cursor: pointer;
  position: relative;
  border: 1px solid var(--border-color);
  transition: all 0.3s;
}

.card.added {
  border-color: var(--primary-color);
}

.card:active {
  transform: scale(0.96);
}

.card-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 8px;
  border-radius: 50%;
  background-color: var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.card-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.card-desc {
  font-size: 10px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-tag {
  position: absolute;
  top: 4px;
  right: 4px;
}

.indicator-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--border-color);
  cursor: pointer;
  transition: all 0.3s;
}

.dot.active {
  width: 20px;
  border-radius: 4px;
  background-color: var(--primary-color, #23b1c0);
}

.tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: calc(56px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  background-color: var(--card-background);
  box-shadow: 0 -2px 8px var(--shadow-light);
  z-index: 100;
  border-top: 1px solid var(--border-color);
  box-sizing: border-box;
}

.tab-bar .tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  cursor: pointer;
}

.tab-bar .tab-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 2px;
  opacity: 0.5;
  transition: all 0.3s;
}

.tab-bar .tab-text {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
}

.tab-bar .tab-item.active .tab-icon {
  opacity: 1;
  filter: brightness(0) saturate(100%) invert(46%) sepia(79%) saturate(333%)
    hue-rotate(142deg) brightness(91%) contrast(89%);
}

.tab-bar .tab-item.active .tab-text {
  color: var(--primary-color);
  font-weight: 600;
}

.footer {
  padding: 12px;
  background-color: var(--card-background);
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
  border-top: 1px solid var(--border-color);
}

@media (max-width: 480px) {
  .logo-img {
    width: 32px;
    height: 32px;
  }

  .app-name {
    font-size: 15px;
  }

  .content {
    padding: 8px 10px 24px;
  }

  .card-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .card {
    padding: 10px 6px;
  }

  .card-icon {
    width: 44px;
    height: 44px;
  }

  .card-icon-img {
    width: 24px;
    height: 24px;
  }

  .card-title {
    font-size: 12px;
  }
}
</style>
